import IHttpService = angular.IHttpService;
import { AuthService } from 'app/_services/auth.service';
import { ErrorHandler } from 'app/_services/error-handler.factory';
import { StorageFcfsRequestResource } from 'app/_resources/storage-fcfs-request.resource';
import { StorageFcfsConfirmationResource } from 'app/_resources/storage-fcfs-confirmation.resource';
import * as angular from 'angular';
import * as moment from 'pkp-moment-timezone-all-years';
import { mgoAngularWizard, StorageBookingWizardContext } from '../custom';

enum PageState {
    WAITING_FOR_SSO,
    NO_RESPONSE_FROM_SSO,
    CAPACITY_NOT_AVAILABLE,
    OKAY,
    OKAY_ALTERNATIVE,
    WAITING_FOR_CONFIRMATION,
    CONFIRMATION_TIMEOUT,
    CONFIRMATION_REJECTED,
    CONFIRMATION_ACCEPTED
}

class StorageBookingWizardFcfsOfferCtrl implements ng.IController {
    public pageStateEnum = PageState;
    public state: PageState;
    public selectedAlt: number = 1;
    public loading: boolean = true;
    public isLoggedIn: boolean;
    public form: ng.IFormController;

    private wizardCtrl: mgoAngularWizard.Wizard<StorageBookingWizardContext>;
    private abortProgressbar = false;
    private abortProgressbarConfirmation = false;
    private count: number;

    private userSelectedAlternative: boolean | null = null;

    constructor(
        private authService: AuthService,
        private errorHandler: ErrorHandler,
        private storageFcfsRequestResource: StorageFcfsRequestResource,
        private storageFcfsConfirmationResource: StorageFcfsConfirmationResource,
        private $http: IHttpService
    ) {
        this.isLoggedIn = this.authService.getLoggedInUser() != null;
        this.count = 0;
    }

    $onInit() {
        this.loading = false;
        this.state = PageState.WAITING_FOR_SSO;

        this.storageFcfsRequestResource
            .getResponseForRequest(
                { id: this.wizardCtrl.context.storageFcfsRequest.id },
                (res: StorageFcfsResponseDto) => {
                    this.wizardCtrl.context.storageFcfsResponse = res;
                    if (
                        this.wizardCtrl.context.storageFcfsResponse == null ||
                        this.wizardCtrl.context.storageFcfsResponse
                            .responseId == null
                    ) {
                        this.state = PageState.NO_RESPONSE_FROM_SSO;
                    } else if (
                        this.wizardCtrl.context.storageFcfsResponse
                            .responseId != null &&
                        (this.wizardCtrl.context.storageFcfsResponse
                            .components == null ||
                            this.wizardCtrl.context.storageFcfsResponse
                                .components.length == 0)
                    ) {
                        this.state = PageState.CAPACITY_NOT_AVAILABLE;
                    } else {
                        this.state = this.checkResponseState();
                    }
                },
                (err) => {
                    this.errorHandler(err);
                }
            )
            .$promise.finally(() => {
                this.abortProgressbar = true;
                this.state =
                    this.state == PageState.WAITING_FOR_SSO
                        ? PageState.NO_RESPONSE_FROM_SSO
                        : this.state;
            });
    }

    public confirmBooking(form: ng.IFormController): boolean {
        if (!form.$valid) {
            return false;
        }
        this.userSelectedAlternative = this.state == PageState.OKAY_ALTERNATIVE;

        this.wizardCtrl.context.storageFcfsConfirmation = <
            StorageFcfsConfirmationDto
        >{};
        let tmpConfirmation: StorageFcfsConfirmationDto = {
            id: null,
            responseId: this.wizardCtrl.context.storageFcfsResponse.id,
            timestamp: moment(Date.now()).toISOString(),
            userEmail: this.authService.getLoggedInUser().email,
            shipperEic: '',
            ssoRefId: this.wizardCtrl.context.storageFcfsResponse.ssoId,
            state: 'UNRESPONDED'
        };
        this.storageFcfsConfirmationResource
            .uploadConfirmation(
                tmpConfirmation,
                (res: StorageFcfsConfirmationDto) => {
                    this.wizardCtrl.context.storageFcfsConfirmation = res;
                    return true;
                },
                (err) => {
                    this.errorHandler(err);
                }
            )
            .$promise.finally(() => this.confirmationSent());
        return true;
    }

    public confirmationSent(): void {
        if (this.wizardCtrl.context.storageFcfsConfirmation.id) {
            this.state = PageState.WAITING_FOR_CONFIRMATION;
            let pingState;

            this.$http.defaults.headers.get = { count: this.count };
            this.storageFcfsConfirmationResource
                .get(
                    { id: this.wizardCtrl.context.storageFcfsConfirmation.id },
                    (data, headers, status) => {
                        pingState = status;
                        if (status !== 200) {
                            if (angular.isDefined(headers('count'))) {
                                this.count = headers('count');
                            }
                            this.confirmationSent();
                        } else {
                            this.wizardCtrl.context.storageFcfsConfirmation =
                                JSON.parse(data.data);
                            this.finishBooking();
                        }
                    },
                    (err) => {
                        if (this.count < 180) {
                            this.count = +this.count + +30;
                            this.confirmationSent();
                        } else {
                            this.errorHandler(err);
                        }
                    }
                )
                .$promise.finally(() => {
                    if (angular.isDefined(pingState) && pingState == 200) {
                        this.abortProgressbarConfirmation = true;
                    }
                });
        }
    }

    public retryBooking(): void {
        this.wizardCtrl.goTo(1);
    }

    private finishBooking(): void {
        if (
            this.wizardCtrl.context.storageFcfsConfirmation == null ||
            this.wizardCtrl.context.storageFcfsConfirmation.state == null ||
            this.wizardCtrl.context.storageFcfsConfirmation.state ==
                'UNRESPONDED'
        ) {
            this.state = PageState.CONFIRMATION_TIMEOUT;
            this.wizardCtrl.context.finishState = 1;
        } else if (
            this.wizardCtrl.context.storageFcfsConfirmation.state == 'REJECTED'
        ) {
            this.state = PageState.CONFIRMATION_REJECTED;
            this.wizardCtrl.context.finishState = 2;
        } else if (
            this.wizardCtrl.context.storageFcfsConfirmation.state == 'ACCEPTED'
        ) {
            this.state = PageState.CONFIRMATION_ACCEPTED;
            this.wizardCtrl.context.finishState = 3;
        }
        this.wizardCtrl.context.successfulBooking = true;
        this.wizardCtrl.finish();
    }

    public newBooking(): void {
        delete this.wizardCtrl.context.storageFcfsConfirmation;
        delete this.wizardCtrl.context.storageFcfsResponse;
        delete this.wizardCtrl.context.storageFcfsRequest;
        delete this.wizardCtrl.context.selectedFcfsOffer;
        this.wizardCtrl.reset();
        this.wizardCtrl.goTo(0);
    }

    private checkResponseState() {
        if (
            this.wizardCtrl.context.storageFcfsResponse.components.length != 1
        ) {
            return PageState.OKAY_ALTERNATIVE;
        }
        if (
            this.wizardCtrl.context.storageFcfsRequest.quantity.value !=
            this.wizardCtrl.context.storageFcfsResponse.components[0]
                .availableQuantity.value
        ) {
            return PageState.OKAY_ALTERNATIVE;
        }
        if (
            this.wizardCtrl.context.storageFcfsRequest.runtimeStart !=
            this.wizardCtrl.context.storageFcfsResponse.components[0]
                .runtimeStart
        ) {
            return PageState.OKAY_ALTERNATIVE;
        }
        if (
            this.wizardCtrl.context.storageFcfsRequest.runtimeEnd !=
            this.wizardCtrl.context.storageFcfsResponse.components[0].runtimeEnd
        ) {
            return PageState.OKAY_ALTERNATIVE;
        }
        if (
            this.wizardCtrl.context.selectedFcfsOffer.components[0]
                .capacityCategory !=
            this.wizardCtrl.context.storageFcfsResponse.components[0]
                .capacityCategory
        ) {
            return PageState.OKAY_ALTERNATIVE;
        }
        if (
            this.wizardCtrl.context.selectedFcfsOffer.components[0]
                .availableQuantity.unit !=
            this.wizardCtrl.context.storageFcfsResponse.components[0]
                .availableQuantity.unit
        ) {
            return PageState.OKAY_ALTERNATIVE;
        }
        return PageState.OKAY;
    }
}

angular.module('app').component('pkpStorageBookingWizardFcfsoffer', {
    controller: StorageBookingWizardFcfsOfferCtrl,
    template:
        require('app/storage-booking-wizard/storage-booking-wizard-fcfsoffer.component.html'),
    require: {
        wizardCtrl: '^^wizard'
    },
    bindings: {}
});
